import type { TFunction } from 'i18next'

import {
  ItemDirection,
  ItemType,
} from '#/shared/api/generated/communication/models'
import type { MessageHit } from '#/shared/api/generated/communication/models'
import type { CustomerDeliverability } from '#/entities/number-health'

import type { ActivityIcon } from '../model/types'
import { counterpartyTitle } from './customer-display'
import { activityTimestamp, dateGroupLabel } from './relative-date'

/** A message/transcript search hit, ready for the results list. */
export interface MessageHitRow {
  /** Timeline item id — anchors the thread when the row is opened. */
  id: string
  conversationId: string
  /** Counterparty E.164, so the thread can still be opened by number. */
  phone?: string
  /** Counterparty name (or number), suffixed with the shared mainline label. */
  title: string
  /** Server-built excerpt centred on the match — highlighted at render time. */
  snippet: string
  /** Right-aligned time label, e.g. "9:45 AM". */
  timestamp: string
  /** Date bucket label used to group consecutive hits, e.g. "Today". */
  dateGroup: string
  /** The hit is the agent's own message — the snippet reads "You: …". */
  outbound: boolean
  /** Avatar glyph; call hits (matched on their transcript) show a call icon. */
  icon?: ActivityIcon
  /** Inline SMS deliverability from the resolved customer. */
  deliverability?: CustomerDeliverability | null
}

/** Avatar glyph for a hit: calls matched on their transcript keep the call
 *  direction glyph; SMS/MMS hits fall back to the counterparty's initials. */
function hitIcon(hit: MessageHit): ActivityIcon | undefined {
  if (hit.type !== ItemType.call) return undefined
  return hit.direction === ItemDirection.outbound
    ? 'call-outbound'
    : 'call-inbound'
}

/** Map a search hit to a results row. Mirrors the thread rows' title rule so a
 *  chat and a message hit for the same counterparty read identically. */
export function messageHitToRow(hit: MessageHit, t: TFunction): MessageHitRow {
  const base = counterpartyTitle(hit.customer, hit.counterparty_number)
  const label = hit.mainline?.label
  return {
    id: hit.id,
    conversationId: hit.conversation_id,
    phone: hit.counterparty_number ?? undefined,
    title: label ? `${base} · ${label}` : base,
    snippet: hit.snippet,
    timestamp: activityTimestamp(hit.occurred_at),
    dateGroup: dateGroupLabel(hit.occurred_at, t),
    outbound: hit.direction === ItemDirection.outbound,
    icon: hitIcon(hit),
    deliverability: hit.customer,
  }
}
